import {
    AiGeneratedLabel,
    Alert,
    AlertDescription,
    AlertTitle,
    Button,
    Progress,
    Text,
} from '../design-system/components';
import { CircleAlert, CircleCheck, LoaderCircle, Mail } from 'lucide-react';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../design-system/components/ui/tabs';
import {
    useAlertContexts,
    useGenerateAlertPreview,
    useGeneratedAlerts,
    useSendTestAlert,
    useTaskStatus,
} from 'utils/hooks/alerts';
import { useEffect, useMemo, useRef, useState } from 'react';
import GeneratedAlert from './Alerts/GeneratedAlert';
import { parseReceiverEmails } from './Alerts/receiverEmails';
import NiceModal from '@ebay/nice-modal-react';
import SendTestConfirmModal, {
    SEND_TEST_CHOICE,
} from '@/ui/components/modals/alerts/SendTestConfirmModal';
import { cn } from '../design-system/utils/cn';
import { mixpanelTrack } from 'utils/app';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';

const TASK_PROGRESS_ICONS = {
    creating: LoaderCircle,
    running: LoaderCircle,
    done: CircleCheck,
    failed: CircleAlert,
};

const TASK_PROGRESS_VARIANTS = {
    creating: 'info',
    running: 'info',
    done: 'success',
    failed: 'destructive',
};

const CONTEXT_STATUS_SORT_ORDER = {
    ready: 0,
    failed: 1,
    generating: 2,
    pending: 3,
};

const CONTEXT_STATUS_BADGE_STYLE = {
    failed: 'bg-rose-100 text-rose-700',
    generating: 'bg-amber-100 text-amber-700',
    pending: 'bg-slate-100 text-slate-700',
};

const CONTEXT_STATUS_LABEL = {
    pending: 'Needs regeneration',
    generating: 'Generating',
    failed: 'Failed',
};

const CONTEXT_TYPE_LABEL = {
    brand: 'Brand',
    category: 'Topic',
    topic: 'Topic',
};

function parseTerritorySections(territoryText) {
    if (!territoryText) return [];
    const lines = territoryText.split('\n');
    const sections = [];
    let currentSection = null;

    lines.forEach((line) => {
        if (line.startsWith('#')) {
            if (currentSection) {
                sections.push(currentSection);
            }
            currentSection = {
                heading: line.replace(/^#+\s*/, ''),
                paragraphs: [],
            };
            return;
        }
        if (line.trim() === '') {
            return;
        }
        if (!currentSection) {
            currentSection = {
                heading: null,
                paragraphs: [],
            };
        }
        currentSection.paragraphs.push(line);
    });

    if (currentSection) {
        sections.push(currentSection);
    }
    return sections;
}

function TaskProgress({ taskId, onTaskComplete }) {
    const { data: taskStatus } = useTaskStatus(taskId);
    const taskIdCompleteRef = useRef(null);

    useEffect(() => {
        if (
            taskStatus?.status === 'done' &&
            taskIdCompleteRef.current !== taskStatus.task_id
        ) {
            onTaskComplete(taskStatus.task_id);
            taskIdCompleteRef.current = taskStatus.task_id;
        }
    }, [taskStatus, onTaskComplete]);

    if (!taskStatus)
        return (
            <Alert variant="info" className="gap-3">
                <div className="flex gap-1 items-center w-full">
                    <LoaderCircle className="h-4 w-4 shrink-0 self-baseline mt-[2px] animate-spin" />
                    <div>
                        <AlertTitle className="text-sm font-medium leading-5 normal-case">
                            Loading preview status...
                        </AlertTitle>
                        <Progress value={1} className="h-2" />
                    </div>
                </div>
            </Alert>
        );

    const AlertIcon = TASK_PROGRESS_ICONS[taskStatus.status];

    return (
        <Alert
            variant={TASK_PROGRESS_VARIANTS[taskStatus.status]}
            className="gap-3"
        >
            <div className="flex gap-1 items-center w-full">
                <AlertIcon
                    className={cn('h-4 w-4 shrink-0 self-baseline mt-[2px]', {
                        'animate-spin': ['creating', 'running'].includes(
                            taskStatus.status,
                        ),
                    })}
                />
                <div className="flex gap-1 flex-col w-full">
                    {taskStatus.status === 'creating' ||
                    taskStatus.status === 'running' ? (
                        <>
                            <AlertTitle className="text-sm font-medium leading-5 normal-case">
                                Preview is generating
                            </AlertTitle>
                            <AlertDescription className="text-sm leading-5 normal-case">
                                It can take up to 3 minutes to see results
                            </AlertDescription>
                            <Progress
                                value={
                                    taskStatus.progress !== null &&
                                    taskStatus.progress !== undefined
                                        ? taskStatus.progress * 100
                                        : 1
                                }
                                className="h-2"
                            />
                        </>
                    ) : taskStatus.status === 'failed' &&
                      taskStatus.exception === 'NoTopicFound' ? (
                        <AlertTitle className="text-sm font-medium leading-5 normal-case">
                            No relevant trends found for the selected categories
                            and trend type. Please adjust your alert
                            configuration and try again.
                        </AlertTitle>
                    ) : taskStatus.status === 'failed' ? (
                        <AlertTitle className="text-sm font-medium leading-5 normal-case">
                            Failed to generate preview. Please try again.
                        </AlertTitle>
                    ) : null}
                </div>
            </div>
        </Alert>
    );
}

export function ContextTerritoryCard({ context }) {
    const sections = useMemo(
        () => parseTerritorySections(context.territory_text),
        [context.territory_text],
    );
    const status = context.territory_status || 'pending';

    return (
        <div className="rounded-lg border border-base-card bg-background p-5 flex flex-col gap-4 font-sans">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <Text
                    as="p"
                    weight="medium"
                    size="base"
                    className="!leading-6 font-sans"
                >
                    {context.name}
                </Text>
                <div className="flex items-center gap-2">
                    <span
                        className={cn(
                            'text-[11px] px-2 py-1 rounded-full',
                            context.context_type === 'brand'
                                ? 'bg-violet-100 text-violet-700'
                                : 'border border-[#D8EBFF] bg-[#EAF5FF] text-[#1C78F2]',
                        )}
                    >
                        {CONTEXT_TYPE_LABEL[context.context_type] ||
                            context.context_type}
                    </span>
                    {status !== 'ready' ? (
                        <span
                            className={cn(
                                'text-[11px] px-2 py-1 rounded-full capitalize',
                                CONTEXT_STATUS_BADGE_STYLE[status] ||
                                    'bg-slate-100 text-slate-700',
                            )}
                        >
                            {CONTEXT_STATUS_LABEL[status] || status}
                        </span>
                    ) : null}
                </div>
            </div>

            {status === 'ready' && sections.length > 0 ? (
                <div
                    className="flex flex-col gap-4"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                    <AiGeneratedLabel />
                    {sections.map((section, sectionIndex) => (
                        <div
                            key={sectionIndex}
                            className="flex flex-col gap-[10px]"
                        >
                            {section.heading ? (
                                <Text
                                    as="p"
                                    weight="medium"
                                    size="base"
                                    className="!leading-6"
                                    style={{ fontFamily: 'inherit' }}
                                >
                                    {section.heading}
                                </Text>
                            ) : null}
                            {section.paragraphs.map((paragraph, pIndex) => (
                                <Text
                                    key={pIndex}
                                    as="p"
                                    size="sm"
                                    className="!leading-5"
                                    style={{ fontFamily: 'inherit' }}
                                >
                                    {paragraph}
                                </Text>
                            ))}
                        </div>
                    ))}
                </div>
            ) : null}

            {status === 'ready' && sections.length === 0 ? (
                <Text as="span" size="sm" color="muted">
                    Territory generated but no formatted sections were found.
                </Text>
            ) : null}

            {status === 'generating' || status === 'pending' ? (
                <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
                    <LoaderCircle className="h-4 w-4 animate-spin text-muted-foreground" />
                    <Text as="span" size="sm" color="muted">
                        {status === 'generating'
                            ? 'AI is generating context territory.'
                            : 'Context territory needs regeneration.'}
                    </Text>
                </div>
            ) : null}

            {status === 'failed' ? (
                <div className="flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2">
                    <CircleAlert className="h-4 w-4 text-rose-600" />
                    <Text as="span" size="sm" className="text-rose-700">
                        Context territory failed to generate. Edit and save this
                        context to retry.
                    </Text>
                </div>
            ) : null}
        </div>
    );
}

function GeneratedContextsPreview({ contextIds }) {
    const { data: contextsData, isLoading } = useAlertContexts();
    const contexts = contextsData?.contexts || [];
    const selectedContextIds = contextIds || [];

    const selectedContexts = useMemo(() => {
        const byId = new Map(
            contexts.map((ctx) => [ctx.alert_context_id, ctx]),
        );
        return selectedContextIds
            .map((contextId) => byId.get(contextId))
            .filter(Boolean);
    }, [contexts, selectedContextIds]);

    const sortedContexts = useMemo(() => {
        return [...selectedContexts].sort((a, b) => {
            const statusA = CONTEXT_STATUS_SORT_ORDER[a.territory_status] ?? 99;
            const statusB = CONTEXT_STATUS_SORT_ORDER[b.territory_status] ?? 99;
            if (statusA !== statusB) {
                return statusA - statusB;
            }
            return a.name.localeCompare(b.name);
        });
    }, [selectedContexts]);

    if (isLoading) {
        return (
            <div className="max-w-[760px] mx-auto flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
                <LoaderCircle className="h-4 w-4 animate-spin text-muted-foreground" />
                <Text as="span" size="sm" color="muted">
                    Loading selected contexts...
                </Text>
            </div>
        );
    }

    if (!sortedContexts.length) {
        return (
            <div className="max-w-[760px] mx-auto rounded-md border bg-muted/40 p-4">
                <Text as="span" size="sm" color="muted">
                    Selected contexts will show up here.
                </Text>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4 max-w-[760px] mx-auto">
            {sortedContexts.map((context) => (
                <ContextTerritoryCard
                    key={context.alert_context_id}
                    context={context}
                />
            ))}
        </div>
    );
}

function PreviewStatus({
    taskId,
    onTaskComplete,
    latestPreview,
    hasGeneratingSelectedContext = false,
}) {
    if (taskId) {
        return <TaskProgress taskId={taskId} onTaskComplete={onTaskComplete} />;
    }
    if (hasGeneratingSelectedContext) {
        return (
            <Alert variant="info" className="gap-3">
                <div className="flex gap-1 items-center w-full">
                    <LoaderCircle className="h-4 w-4 shrink-0 self-baseline mt-[2px] animate-spin" />
                    <div className="flex gap-1 flex-col w-full">
                        <AlertTitle className="text-sm font-medium leading-5 normal-case">
                            AI is generating context
                        </AlertTitle>
                        <AlertDescription className="text-sm leading-5 normal-case">
                            It can take up to 3 minutes to see results
                        </AlertDescription>
                    </div>
                </div>
            </Alert>
        );
    }
    if (latestPreview) {
        return (
            <Alert variant="info" className="gap-3">
                <div className="flex gap-1 items-center w-full">
                    <CircleCheck className="h-4 w-4 shrink-0 self-baseline mt-[2px]" />
                    <AlertTitle className="text-sm font-medium leading-5 normal-case">
                        Preview is up to date
                    </AlertTitle>
                </div>
            </Alert>
        );
    }
    return (
        <Alert variant="info" className="gap-3">
            <div className="flex gap-1 items-center w-full">
                <CircleCheck className="h-4 w-4 shrink-0 self-baseline mt-[2px]" />
                <AlertTitle className="text-sm font-medium leading-5 normal-case">
                    Create alert to see a preview
                </AlertTitle>
            </div>
        </Alert>
    );
}

function AlertPreviewNew({ alertConfigId, updatedAt, config }) {
    const qc = useQueryClient();
    const [taskId, setTaskId] = useState(null);
    const [activeTab, setActiveTab] = useState('preview');
    const generatePreview = useGenerateAlertPreview();
    const send = useSendTestAlert();
    const generatePreviewRef = useRef(null);
    const { data: generatedAlerts, isLoading: alertsLoading } =
        useGeneratedAlerts();
    const { data: contextsData, isLoading: contextsLoading } =
        useAlertContexts();
    const taskStatus = useTaskStatus(taskId)?.data;
    const selectedContextIds = config?.context_ids || [];
    const hasSelectedContexts = selectedContextIds.length > 0;
    const selectedContexts = useMemo(() => {
        const contexts = contextsData?.contexts || [];
        const byId = new Map(
            contexts.map((ctx) => [ctx.alert_context_id, ctx]),
        );
        return selectedContextIds
            .map((contextId) => byId.get(contextId))
            .filter(Boolean);
    }, [contextsData, selectedContextIds]);
    const hasGeneratingSelectedContext = selectedContexts.some(
        (ctx) => ctx?.territory_status === 'generating',
    );
    const selectedContextNames = useMemo(() => {
        return selectedContexts.map((ctx) => ctx?.name).filter(Boolean);
    }, [selectedContexts]);
    const previewBrandName = selectedContextNames.length
        ? selectedContextNames.join(', ')
        : config?.brand_name;
    const isAlertPreviewGenerating = !!(
        alertsLoading ||
        generatePreview.isLoading ||
        (taskId && !['done', 'failed'].includes(taskStatus?.status))
    );

    useEffect(() => {
        mixpanelTrack('Alert Preview', { alertConfigId });
    }, [alertConfigId]);

    const previews =
        generatedAlerts?.alerts?.filter(
            (alert) => alert.alert_config_id === alertConfigId,
        ) || [];
    const latestPreview = previews.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
    )[0];
    const previewIsUpToDate =
        latestPreview &&
        new Date(latestPreview?.created_at) >= new Date(updatedAt);

    if (
        !previewIsUpToDate &&
        updatedAt &&
        alertConfigId &&
        !taskId &&
        !generatePreview.isLoading &&
        !contextsLoading &&
        !hasGeneratingSelectedContext &&
        !alertsLoading &&
        generatePreviewRef.current !== updatedAt
    ) {
        generatePreviewRef.current = updatedAt;
        generatePreview
            .mutateAsync(alertConfigId)
            .then((response) => {
                setTaskId(response.task_id);
            })
            .catch((error) => {
                console.error('Failed to generate preview:', error);
                toast.error('Failed to generate alert preview');
            });
    }
    const handleTaskComplete = async () => {
        setTaskId(null);
        qc.invalidateQueries('generatedAlerts');
    };

    const handleSend = async () => {
        const choice = await NiceModal.show(SendTestConfirmModal, {
            recipients: parseReceiverEmails(config?.receiver_emails),
        });
        if (choice === SEND_TEST_CHOICE.CANCEL) return;
        if (choice !== SEND_TEST_CHOICE.ALL && choice !== SEND_TEST_CHOICE.ME) {
            throw new Error(`Unexpected send test resolution: ${choice}`);
        }
        const sendToAll = choice === SEND_TEST_CHOICE.ALL;
        toast.promise(
            send.mutateAsync({
                alertPreviewId: latestPreview.alert_id,
                sendToAll,
            }),
            {
                loading: 'Sending test...',
                success: sendToAll
                    ? 'Test sent to all recipients!'
                    : 'Test sent to you!',
                error: 'Failed to send test',
            },
        );
    };
    return (
        <div className="md:relative">
            <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="min-h-[calc(100vh-4rem)] md:min-h-0 md:absolute md:inset-0 flex flex-col"
            >
                <header className="bg-base-muted px-5 py-4 border-b border-base-card flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <Text as="span" className="font-semibold">
                            Alert preview
                        </Text>
                        <TabsList>
                            <TabsTrigger value="preview">
                                Alert preview
                            </TabsTrigger>
                            <TabsTrigger value="contexts">
                                Contexts preview
                            </TabsTrigger>
                        </TabsList>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        className="h-9"
                        disabled={!latestPreview}
                        loading={send.isLoading}
                        onClick={handleSend}
                    >
                        <Mail className="mr-1" />
                        Send Alert
                    </Button>
                </header>
                <main className="bg-base-muted grow flex flex-col">
                    {!(activeTab === 'contexts' && hasSelectedContexts) ? (
                        <div className="p-5">
                            <div className="max-w-[760px] mx-auto">
                                {activeTab === 'contexts' ? (
                                    <Alert variant="info" className="gap-3">
                                        <div className="flex gap-1 items-center w-full">
                                            <CircleCheck className="h-4 w-4 shrink-0 self-baseline mt-[2px]" />
                                            <AlertTitle className="text-sm font-medium leading-5 normal-case">
                                                Add contexts to view preview
                                            </AlertTitle>
                                        </div>
                                    </Alert>
                                ) : (
                                    <>
                                        <PreviewStatus
                                            taskId={taskId}
                                            onTaskComplete={handleTaskComplete}
                                            latestPreview={latestPreview}
                                            hasGeneratingSelectedContext={
                                                hasGeneratingSelectedContext
                                            }
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    ) : null}
                    <TabsContent
                        value="preview"
                        className="grow flex flex-col data-[state=inactive]:hidden"
                    >
                        <GeneratedAlert
                            alert={latestPreview}
                            isLoading={isAlertPreviewGenerating}
                            brandName={previewBrandName}
                            categories={config?.categories}
                        />
                    </TabsContent>
                    <TabsContent
                        value="contexts"
                        className="grow relative data-[state=inactive]:hidden"
                    >
                        <div className="absolute inset-0 overflow-y-auto p-6">
                            <GeneratedContextsPreview
                                contextIds={config?.context_ids}
                            />
                        </div>
                    </TabsContent>
                </main>
            </Tabs>
        </div>
    );
}

export default AlertPreviewNew;
